import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart, Info, TriangleAlert, Users, X } from "lucide-react";
import { Avatar, Button, Field, FieldLabel, IconButton, Textarea } from "@akuna/ui";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { usePatientSearchForQuickStart, type PatientSearchOption } from "@/hooks/usePatientSearchForQuickStart";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/utils/string-utils";
import { supabase } from "@/integrations/supabase/client";
import { splitRelationshipRows, type PetRelationshipKind } from "../petProfileModel";
import { useDeletePetRelationship, useUpsertPetRelationship } from "../usePetProfileMutations";
import { usePetProfileUndo } from "../usePetProfileUndo";
import type { PetRelationshipSummary } from "../usePetReservationSummary";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

const petPhotoUrl = (path: string | null | undefined) =>
  path ? supabase.storage.from("pet-photos").getPublicUrl(path).data.publicUrl : undefined;

export type PetRelationshipsSectionProps = {
  petId: string;
  customerId: string | null;
  relationships: PetRelationshipSummary[];
  compact?: boolean;
  showEmpty?: boolean;
};

export function PetRelationshipsSection({ petId, customerId, relationships, compact, showEmpty }: PetRelationshipsSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const upsertRelationship = useUpsertPetRelationship();
  const deleteRelationship = useDeletePetRelationship();
  const { removeWithUndo } = usePetProfileUndo();

  const [search, setSearch] = useState("");
  const [selectedPet, setSelectedPet] = useState<PatientSearchOption | null>(null);
  const [kind, setKind] = useState<PetRelationshipKind>("likes");
  const [description, setDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState("");

  const petSearch = usePatientSearchForQuickStart(search);
  const petOptions = useMemo(
    () => (petSearch.data ?? []).filter((option) => option.petId !== petId),
    [petId, petSearch.data],
  );
  const petByValue = useMemo(
    () => new Map(petOptions.map((option) => [option.value, option])),
    [petOptions],
  );

  const { likes, dislikes, neutral } = splitRelationshipRows(relationships);

  if (compact && !showEmpty && !isEditing && relationships.length === 0) return null;

  const fail = () => toast({ title: t("petProfile.saveError"), variant: "destructive" });

  const add = async () => {
    if (!selectedPet || selectedPet.petId === petId || !customerId) return;
    try {
      await upsertRelationship.mutateAsync({
        customerId,
        petId,
        relatedPetId: selectedPet.petId,
        relationship: kind,
        description: description.trim() || null,
      });
      setSelectedPet(null);
      setSearch("");
      setDescription("");
    } catch {
      fail();
    }
  };

  const saveDescription = async (relationship: PetRelationshipSummary) => {
    if (!customerId) return;
    try {
      await upsertRelationship.mutateAsync({
        customerId,
        petId,
        relatedPetId: relationship.relatedPetId,
        relationship: relationship.relationship,
        description: editingDescription.trim() || null,
      });
      setEditingId(null);
    } catch {
      fail();
    }
  };

  const remove = (relationship: PetRelationshipSummary) =>
    removeWithUndo({
      message: t("petProfile.relationshipRemoved"),
      remove: () => deleteRelationship.mutateAsync({ id: relationship.id, petId }),
      restore: () => {
        if (!customerId) return Promise.resolve();
        return upsertRelationship.mutateAsync({
          customerId,
          petId,
          relatedPetId: relationship.relatedPetId,
          relationship: relationship.relationship,
        });
      },
    });

  const renderRelationship = (relationship: PetRelationshipSummary) => {
    const positive = relationship.relationship === "likes";
    const negative = relationship.relationship === "dislikes";
    const ToneIcon = positive ? Heart : negative ? TriangleAlert : Users;
    const tagBody = (
      <>
        <ToneIcon className={cn("h-4 w-4 shrink-0", positive ? "text-state-success" : negative ? "text-state-danger" : "text-muted-foreground")} aria-hidden="true" />
        {relationship.description ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={t("petProfile.relationshipDescriptionTooltipLabel", { name: relationship.petName })}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{relationship.description}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        <Avatar className="h-6 w-6 shrink-0" name={relationship.petName} src={petPhotoUrl(relationship.petPhotoPath)} fallback={initials(relationship.petName)} />
        <span className="min-w-0 flex-1 truncate text-xs font-medium">{relationship.petName}</span>
      </>
    );

    return (
      <li
        key={relationship.id}
        className={cn(
          "group/row flex min-w-0 items-center gap-2 rounded-full border py-0.5 px-2 text-[11px]",
          positive ? "border-state-success/30 bg-state-success-soft/40" : negative ? "border-state-danger/30 bg-state-danger-soft/40" : "border-border bg-muted/50",
        )}
      >
        {isEditing ? (
          <Popover
            open={editingId === relationship.id}
            onOpenChange={(open) => {
              setEditingId(open ? relationship.id : null);
              setEditingDescription(open ? relationship.description ?? "" : "");
            }}
          >
            <PopoverTrigger asChild>
              <div
                role="button"
                tabIndex={0}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                aria-label={relationship.petName}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setEditingId(relationship.id);
                    setEditingDescription(relationship.description ?? "");
                  }
                }}
              >
                {tagBody}
              </div>
            </PopoverTrigger>
            <PopoverContent className="grid gap-2" onOpenAutoFocus={(event) => event.preventDefault()}>
              <p className="text-sm font-medium">{t("petProfile.relationshipEditDescriptionTitle")}</p>
              <Textarea
                value={editingDescription}
                onChange={(event) => setEditingDescription(event.target.value)}
                placeholder={t("petProfile.relationshipDescriptionPlaceholder")}
                rows={2}
              />
              <Button variant="outline" size="sm" onClick={() => void saveDescription(relationship)}>
                {t("petProfile.saveRelationshipDescription")}
              </Button>
            </PopoverContent>
          </Popover>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">{tagBody}</div>
        )}
        <IconButton variant="ghost" size="icon-sm" className="h-4 w-4 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100 group-focus-within/row:opacity-100" label={t("petProfile.removeRelationship", { name: relationship.petName })} onClick={() => void remove(relationship)}>
          <X className="h-3 w-3" aria-hidden="true" />
        </IconButton>
      </li>
    );
  };

  return (
    <PetProfileSectionShell
      title={t("petProfile.relationships")}
      icon={Users}
      tone="blue"
      count={relationships.length}
      fullWidth
      isEditing={isEditing}
      onToggleEdit={() => setIsEditing((current) => !current)}
      editLabel={t("petProfile.editRelationships")}
      doneLabel={t("petProfile.doneRelationships")}
      compact={compact && !isEditing}
    >
      {relationships.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.relationshipsEmpty")}</PetProfileSectionEmpty>
      ) : compact && !isEditing ? (
        <div className="grid gap-2">
          {likes.length > 0 ? <div className="grid gap-1"><p className="text-xs font-medium text-state-success">{t("petProfile.relationshipLikes")}</p><ul className="flex flex-wrap gap-1.5">{likes.map(renderRelationship)}</ul></div> : null}
          {dislikes.length > 0 ? <div className="grid gap-1"><p className="text-xs font-medium text-state-danger">{t("petProfile.relationshipDislikes")}</p><ul className="flex flex-wrap gap-1.5">{dislikes.map(renderRelationship)}</ul></div> : null}
          {neutral.length > 0 ? <div className="grid gap-1"><p className="text-xs font-medium text-muted-foreground">{t("petProfile.relationshipNeutral")}</p><ul className="flex flex-wrap gap-1.5">{neutral.map(renderRelationship)}</ul></div> : null}
        </div>
      ) : (
        <ul className="flex gap-2 mb-2">
          {[...likes, ...dislikes, ...neutral].map(renderRelationship)}
        </ul>
      )}

      {isEditing ? <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
        <Field className="min-w-[180px] flex-1">
          <FieldLabel htmlFor="relationship-pet-search">{t("petProfile.relationshipPet")}</FieldLabel>
          <Autocomplete
            id="relationship-pet-search"
            ariaLabel={t("petProfile.relationshipPet")}
            value={search}
            options={petOptions.map((option) => ({ value: option.value, label: option.petName }))}
            onChange={(value) => {
              setSearch(value);
              if (!value) setSelectedPet(null);
            }}
            onSelect={(option) => setSelectedPet(petByValue.get(option.value) ?? null)}
            selectedValue={selectedPet?.value}
            disableLocalFilter
            popoverPortalled={false}
            useInlineDropdown
            placeholder={t("petProfile.relationshipPetPlaceholder")}
            emptyMessage={petSearch.isFetching ? t("loading") : t("petProfile.relationshipPetEmpty")}
            disabled={!customerId}
            renderOption={(option) => {
              const pet = petByValue.get(option.value);
              return (
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar
                    className="h-9 w-9 shrink-0"
                    name={pet?.petName ?? option.label}
                    src={petPhotoUrl(pet?.petPhotoPath)}
                    fallback={initials(pet?.petName ?? option.label)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{pet?.petName ?? option.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[pet?.petBreedName, pet?.ownerName].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </div>
              );
            }}
          />
        </Field>

        <div role="radiogroup" aria-label={t("petProfile.relationshipKind")} className="flex gap-1.5">
          {(
            [
              {
                value: "likes" as const,
                label: t("petProfile.relationshipLikes"),
                Icon: Heart,
                activeClass: "border-state-success bg-state-success-soft text-state-success",
              },
              {
                value: "dislikes" as const,
                label: t("petProfile.relationshipDislikes"),
                Icon: TriangleAlert,
                activeClass: "border-state-danger bg-state-danger-soft text-state-danger",
              },
            ]
          ).map(({ value, label, Icon, activeClass }) => {
            const active = kind === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setKind(value)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                  active ? activeClass : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>

        <Field className="min-w-[220px] flex-1 basis-full">
          <FieldLabel htmlFor="relationship-description">{t("petProfile.relationshipDescriptionLabel")}</FieldLabel>
          <Textarea
            id="relationship-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("petProfile.relationshipDescriptionPlaceholder")}
            rows={2}
          />
        </Field>

        <Button disabled={!selectedPet || !customerId} onClick={() => void add()}>
          {t("petProfile.addRelationship")}
        </Button>
      </div> : null}
    </PetProfileSectionShell>
  );
}
